import type { ReactNode } from 'react';
import type { CombinedEuParts } from '@/features/dossier/api';
import { PanelCard } from './DossierPrimitives';

/**
 * The shared contract between the per-regulation EU dossier profiles
 * (`euMdrDossierProfile` / `euIvdrDossierProfile`) and the shell that renders
 * them (`CombinedEuDossierView`).
 *
 * A *profile* is a declarative description of one regulation's dossier: the
 * numbered Parts (in document order, with their regulation-correct titles +
 * article numbers + class applicability) and the appendices. Each profile owns
 * ONLY its own regulation's Parts — the MDR profile never names an IVDR Part and
 * vice-versa — so editing one cannot affect the other. The shell consumes a
 * profile and never branches on regulation itself. This mirrors the backend,
 * where MDR and IVDR each have their own combined renderer module.
 */

/** Context a Part renderer needs beyond the Part value itself. */
export interface DossierRenderCtx {
  productId: number;
  deviceClass: string;
}

/** One Part in a regulation's dossier profile. */
export interface DossierPartSpec {
  /** Stable anchor id (e.g. `eu-sec-td`). Shared ids (td/pms/doc/udi/psur) let
   *  the shell treat the two regulations uniformly. */
  id: string;
  /** Short label (kept for a future section nav; not rendered today). */
  navTitle: string;
  /** Full human title including the regulation-correct article / annex number. */
  title: string;
  /** Whether this Part applies to the given device class (regulation implicit in
   *  the owning profile). A non-applicable Part is omitted entirely; an
   *  applicable Part with no run yet is shown as "pending". */
  applicable: (deviceClass: string) => boolean;
  /** Locate this Part's value in the assembled payload (null → no run yet). */
  pick: (parts: CombinedEuParts) => unknown;
  /** Render the (present) Part body. */
  render: (part: NonNullable<unknown>, ctx: DossierRenderCtx) => ReactNode;
}

/** A complete per-regulation dossier profile. */
export interface EuDossierProfile {
  regulation: 'MDR' | 'IVDR';
  /** Numbered Parts in document order (Part 1..N). */
  numberedParts: DossierPartSpec[];
  /** Appendices after the numbered Parts (present-only, not numbered). */
  appendices: DossierPartSpec[];
}

/** The common applicability — a Part that applies to every device class. */
export const appliesToAllClasses = (): boolean => true;

// ── Shared part-payload helpers ───────────────────────────────────────────────

/** The `.dossier` key of an annex run output (td/pms/sscp), or null. */
export function pickDossier(part: unknown): Record<string, unknown> | null {
  if (!part || typeof part !== 'object') return null;
  const d = (part as { dossier?: unknown }).dossier;
  return d && typeof d === 'object' ? (d as Record<string, unknown>) : null;
}

/** The `submission_header` sibling of an annex run output, or null. */
export function pickSubmissionHeader(part: unknown): Record<string, unknown> | null {
  if (!part || typeof part !== 'object') return null;
  const h = (part as { submission_header?: unknown }).submission_header;
  return h && typeof h === 'object' ? (h as Record<string, unknown>) : null;
}

// ── Shared muted notes (a card on their own; a plain block inside a Part card) ──

/** Muted "not yet generated" card for an applicable Part with no run yet —
 *  mirrors the combined PDF's `.part-pending` placeholder so the page still
 *  lists the Part, keeping its number aligned with the PDF. */
export function PartPendingNote() {
  return (
    <PanelCard>
      <p className="text-[13px] text-muted italic m-0">
        This part has not yet been generated. Run the corresponding agent to populate it.
      </p>
    </PanelCard>
  );
}

/** Muted "in the PDF" card for a part that has no React renderer yet (the MDR
 *  implant card / SSCP lay summary). */
export function InPdfNote({ title }: { title: string }) {
  return (
    <PanelCard>
      <h3 className="text-[17px] font-bold text-navy mt-0 mb-2">{title}</h3>
      <p className="text-[13px] text-muted m-0">
        This section is included in the downloadable PDF.
      </p>
    </PanelCard>
  );
}
